import React from 'react';
import { useSelector } from 'react-redux';
import { Grid, Typography } from '@mui/material';
import { formatCurrency, formatNumber } from 'utils/utils';
import './FareBreakup.scss';

const N = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v));

// The engine (booking rateModifiers) returns the figures it charged:
// `subtotal` (base fare + fixed charges + the extra lines), `totalAmount`
// (everything, tax included) and a gst breakdown line carrying each
// component's own share. This page used to recompute all three, and left the
// garage charge out of its total while showing only one tax component's
// amount as the whole tax (2026-09-25). Read the engine's numbers; fall back
// to the local sums only when a figure is missing.
const calculateGSTAmount = (tripDetails) => {
  if (!tripDetails) return 0;
  const gstBreakdown = tripDetails.breakdown?.find((item) => item.chargeType === 'gst');
  let gst = N(gstBreakdown?.addedCharge);
  if (!gst || !isFinite(gst) || gst > 1e10) {
    const charges = getTripCharges(tripDetails);
    const percent = N(gstBreakdown?.meta?.breakup?.[0]?.unitValue) || 0;
    gst = (charges * percent) / 100;
  }
  return gst;
};

function formatPrice(value) {
  if (isNaN(value) || value === null || value === undefined) return 0;
  return Number(value).toLocaleString('en-IN');
}

const getBreakdownAmount = (tripDetails, chargeType) => {
  return N(tripDetails?.breakdown?.find((item) => item.chargeType === chargeType)?.addedCharge);
};

const getPackageCost = (tripDetails) => {
  if (!tripDetails) return 0;
  // Backend se cost lo agar valid hai, nahi toh subtotal se nikalo
  const costFromConfig = N(tripDetails?.rateConfig?.cost);
  if (costFromConfig > 0) return costFromConfig;
  const fromSubtotal =
    N(tripDetails?.subtotal) -
    (N(tripDetails?.baseRate) + N(tripDetails?.surCharge) + N(tripDetails?.platformCharge));
  return fromSubtotal > 0 ? fromSubtotal : 0;
};

const getTripCharges = (tripDetails) => {
  if (!tripDetails) return 0;
  // The engine's subtotal is base fare + base rate + surcharge + platform, and
  // the extra lines it charged. Garage, tax, tolls and parking are shown
  // separately below, as they are on the invoice.
  const engineSubtotal = N(tripDetails?.subtotal);
  if (engineSubtotal > 0) {
    const extras =
      tripDetails.breakdown
        ?.filter(
          (item) =>
            ![
              'tollCharges',
              'parkingCharge',
              'discountAmount',
              'gst',
              'tip',
              'pointsRedeem',
              'garageCharge',
            ].includes(item.chargeType),
        )
        ?.reduce((sum, item) => sum + N(item.addedCharge), 0) || 0;
    return engineSubtotal + extras;
  }

  const excludedTypes = [
    'tollCharges',
    'parkingCharge',
    'discountAmount',
    'gst',
    'tip',
    'pointsRedeem',
    'garageCharge',
  ];
  const baseAmount = N(tripDetails.baseRate);
  const packageCost = getPackageCost(tripDetails);
  const surcharge = N(tripDetails.surCharge);
  const platformCharge = N(tripDetails.platformCharge);

  const additionalCharges =
    tripDetails.breakdown
      ?.filter((item) => !excludedTypes.includes(item.chargeType))
      ?.reduce((sum, item) => sum + N(item.addedCharge), 0) || 0;

  return baseAmount + packageCost + surcharge + platformCharge + additionalCharges;
};

const getTotalAmount = (tripDetails) => {
  if (!tripDetails) return 0;
  // `totalAmount` already includes the garage charge, GST, tolls, parking and
  // any discount, so prefer it over re-adding the lines by hand.
  const engineTotal = N(tripDetails?.totalAmount);
  if (engineTotal > 0) return engineTotal;

  const charges = getTripCharges(tripDetails);
  const gst = calculateGSTAmount(tripDetails);
  const discount = Math.abs(getBreakdownAmount(tripDetails, 'discountAmount'));
  const tolls =
    getBreakdownAmount(tripDetails, 'tollCharges') +
    getBreakdownAmount(tripDetails, 'parkingCharge');
  const garage = getBreakdownAmount(tripDetails, 'garageCharge');
  return charges + garage + gst - discount + tolls;
};

function FareBreakUp(props) {
  const defaultRateData = useSelector(
    (state) => state?.userGetTripPrice?.userGetTripPrice?.data?.rate,
  );
  const customRateData = useSelector(
    (state) => state?.rateEstimateReducer?.rateEstimateData?.data?.rate,
  );

  const tripDetails = customRateData || defaultRateData;

  const renderChargeSection = (label, amount, subBreakup = null, isNegative = false) => {
    if (!amount || amount <= 0) return null;

    return (
      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        className="marginbottom20"
      >
        <Grid item>
          <Typography style={{ fontSize: '1.25rem', fontWeight: 500 }}>{label}</Typography>
          {subBreakup}
        </Grid>
        <Grid item>
          <Typography className={`font16 ${isNegative ? 'colorRed' : ''}`}>
            {isNegative ? `-₹${formatPrice(Math.abs(amount))}` : `₹${formatPrice(amount)}`}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderTripChargesBreakup = () => {
    const packageCost = getPackageCost(tripDetails);
    // Flat / flat-plus-km airport cards: the engine returns flatBreakup and a
    // baseRate of 0 (the row's base rate is not charged for these shapes).
    const flat = tripDetails?.flatBreakup;
    const isFlatAirport =
      props?.type == 4 &&
      flat &&
      (flat.pricingType === 'flatRate' || flat.pricingType === 'flatPlusKm');
    const baseFareItems = isFlatAirport
      ? [
          { label: 'Flat Rate', value: N(flat.flatAmount) },
          ...(flat.pricingType === 'flatPlusKm'
            ? [
                {
                  label: `Extra KM (${N(flat.extraKmDistance)} km beyond ${N(flat.includedKm)} km)`,
                  value: N(flat.extraKmFare),
                  ratePer: { lable: 'Rate per KM', value: N(flat.perKm) },
                },
              ]
            : []),
        ]
      : [
          { label: 'Base Rate', value: N(tripDetails?.baseRate) },
          {
            label: props?.type == 1 || props?.type == 4 ? 'Total Base Fare' : 'Package Cost',
            value: packageCost,
            ratePer:
              props?.type == 1 || props?.type == 4
                ? { lable: 'Rate per KM', value: N(tripDetails?.baseRatePerKM) }
                : null,
          },
        ];
    const breakupItems = [
      ...baseFareItems,
      { label: 'Surcharge', value: N(tripDetails?.surCharge) },
      { label: 'Platform Charge', value: N(tripDetails?.platformCharge) },
      {
        label: 'Peak Hour Charges',
        value: getBreakdownAmount(tripDetails, 'peakHour'),
      },
      {
        label: 'Waiting Charges',
        value: getBreakdownAmount(tripDetails, 'waitingTimeCharge'),
      },
      {
        label: 'Cancellation Charges',
        value: getBreakdownAmount(tripDetails, 'cancellationCharge'),
      },
      { label: 'Extra KM', value: getBreakdownAmount(tripDetails, 'extraKm') },
      {
        label: 'Extra Hours',
        value: getBreakdownAmount(tripDetails, 'extraHrs'),
      },
      {
        label: 'Driver Allowance',
        value: getBreakdownAmount(tripDetails, 'driverAllowance'),
      },
      {
        label: 'Night Allowance',
        value: getBreakdownAmount(tripDetails, 'nightAllowance'),
      },
      {
        label: 'Aditional Charges',
        value: getBreakdownAmount(tripDetails, 'additionalCharges'),
      },
      {
        label: 'Placard Charges',
        value: getBreakdownAmount(tripDetails, 'placard'),
      },
    ];

    return (
      <>
        {breakupItems
          .filter((item) => item.value > 0)
          .map((item, index) => (
            <Typography key={index} className="colorGrey font14 fontSemiBold">
              {`${item.label}: ₹${formatPrice(item.value)} ${item.ratePer ? `@(${item.ratePer.lable}: ₹${formatPrice(item.ratePer.value)})` : ''}`}
            </Typography>
          ))}
      </>
    );
  };

  return (
    <div>
      <Grid container direction="column" justifyContent="center" alignItems="start">
        <Grid container item direction="column" className="marginTop25" md={12}>
          <Grid
            container
            item
            direction="column"
            justifyContent="space-between"
            alignItems="center"
            className="billdetails"
            md={12}
          >
            {renderChargeSection(
              'Trip Charges',
              getTripCharges(tripDetails),
              renderTripChargesBreakup(),
            )}

            {renderChargeSection(
              'Points Redeemed',
              getBreakdownAmount(tripDetails, 'pointsRedeem'),
              null,
              true,
            )}

            {renderChargeSection(
              'Tolls & Parking',
              getBreakdownAmount(tripDetails, 'tollCharges') +
                getBreakdownAmount(tripDetails, 'parkingCharge'),
            )}

            {renderChargeSection(
              'Discount',
              Math.abs(getBreakdownAmount(tripDetails, 'discountAmount')),
              null,
              true,
            )}

            {renderChargeSection(
              'GST',
              calculateGSTAmount(tripDetails),
              tripDetails?.breakdown
                ?.find((item) => item.chargeType === 'gst')
                ?.meta?.breakup?.map((tax, idx) => (
                  <Typography key={idx} className="colorGrey font14">
                    {/* each component's own share, not the whole tax */}
                    {`${tax.name.toUpperCase()}: ${tax.unitValue}% (₹${formatPrice(N(tax.totalCharge))})`}
                  </Typography>
                )),
            )}

            {renderChargeSection('Tip', getBreakdownAmount(tripDetails, 'tip'))}

            {N(
              tripDetails?.breakdown?.find((item) => item.chargeType === 'garageCharge')
                ?.addedCharge,
            ) > 0 && (
              <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className="marginbottom20"
              >
                <Grid item>
                  <Typography className="font20">Garage To Garage Charge</Typography>
                  <>
                    <Typography className="colorGrey font12">{`Garage To Pickup: ₹${formatPrice(N(tripDetails.breakdown.find((item) => item.chargeType === 'garageCharge')?.meta?.breakup[0]?.rate))}`}</Typography>
                    <Typography className="colorGrey font12">{`Drop To Garage: ₹${formatPrice(N(tripDetails.breakdown.find((item) => item.chargeType === 'garageCharge')?.meta?.breakup[1]?.rate))}`}</Typography>
                  </>
                </Grid>
                <Grid item>
                  <Typography className="font20">{`₹${formatPrice(N(tripDetails.breakdown.find((item) => item.chargeType === 'garageCharge')?.addedCharge))}`}</Typography>
                </Grid>
              </Grid>
            )}

            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              className="marginbottom20 solidTopLine paddingTop20"
            >
              <Grid item>
                <Typography className="font20 fontMedium">Total</Typography>
                <Typography className="colorGrey font16">Final Amount</Typography>
              </Grid>
              <Grid item>
                <Typography className="font20 fontMedium">{`₹${formatPrice(getTotalAmount(tripDetails))}`}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container className="justifyBetween p10 mtb10">
        <Grid item xs={12}>
          <Grid item container>
            <Grid item className="noteGrid">
              <Typography variant="body3" className="colorPrimary mr15">
                Note -{' '}
              </Typography>
            </Grid>
            <Grid item xs flexDirection="column">
              <Typography variant="body3" className="colorPrimary">
                *Exclusive of Toll & Parking charges
              </Typography>
              <br />
              <Typography variant="body3" className="colorPrimary">
                *The actual bill will be generated based on the actual usage
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}

export default FareBreakUp;
